// The carrier choice rule (2026-10-26, docs/pipeline-behaviors.md "Carrier
// choice lines"), shared by the server and the agent app so one mapping and
// one rule decide it everywhere: a template's pick-one line ("Carrier: UPS /
// USPS / ATS", carrierPickList() in ai.js) becomes the one customer-facing
// carrier CXHub can tell was used, and the other options go.
//
//   1. The tracking numbers in the reply: the order's tracking entries whose
//      number is in the reply, and a number written as "NUM (UPS)" (the form
//      every code tracking fill writes). All one customer-facing carrier ->
//      that carrier.
//   2. Else the order's current shipment's tracking. All one customer-facing
//      carrier -> that carrier, even one the line does not list ("Carrier:
//      FedEx" on a "UPS / USPS" line).
//
// Mixed carriers, a carrier code outside the map, or no tracking at all: the
// line is left exactly as it is; the reply box highlights it and Send refuses
// it until the agent chooses. Never guessed from a tracking number's shape.
//
// Used by server/src/ai/carrier-choice.js (every pipeline draft and the
// agent's reply), template-placeholders.js's fillTrackingPlaceholders() and
// the Templates button insert (ui/src/templateCarrierChoice.js). Pure.
import { carrierPickList, CARRIER_PICK_LIST_SOURCE } from './ai.js'

// TRQ's carrier code -> the name customers see (2026-10-21,
// docs/decisions/2026-10-21-template-tracking-fill.md; reversed for Amazon
// 2026-10-23, docs/decisions/2026-10-23-agent-draft-panel-a2.md). The
// Amazon-delivered carrier (TBA… tracking, TRQ's AMZ) is "AMZ" in everything
// customer-facing and "ATS" internally (server/src/ai/shipments.js's
// INTERNAL_CARRIER_NAMES). A code not here is never guessed: the caller
// leaves the carrier for the agent to choose.
// Codes seen from TRQ (items[].trackingNumbers[].carrier): UPS, USPS, AMZ,
// FedEx / FEDEX. ATS and the Amazon spellings are mapped too because
// normCarrier() in order-ambiguity.js already treats them as the same
// carrier. ONTRAC appears in tests but in no template: not mapped.
export const CUSTOMER_CARRIER_NAMES = Object.freeze({
  UPS: 'UPS',
  USPS: 'USPS',
  AMZ: 'AMZ',
  AMZL: 'AMZ',
  AMAZON: 'AMZ',
  ATS: 'AMZ',
  FEDEX: 'FedEx',
  FDX: 'FedEx',
  FEDERALEXPRESS: 'FedEx',
})

export const carrierCodeKey = (code) => String(code ?? '').toUpperCase().replace(/[\s.-]/g, '')

// The customer-facing name for a TRQ carrier code, or null when unknown.
export function customerCarrierName(code) {
  const key = carrierCodeKey(code)
  return key ? (CUSTOMER_CARRIER_NAMES[key] ?? null) : null
}

const CARRIER_PICK_RAW_RE = new RegExp(CARRIER_PICK_LIST_SOURCE.replaceAll(String.raw`\s`, String.raw`(?:\s|&nbsp;)`), 'i')
// "1Z999AA10123456784 (UPS)": a tracking number with its carrier after it.
const ANNOTATED_NUMBER_RE = /(?<![A-Za-z0-9])([A-Z0-9]{10,40})\s*\(([A-Za-z][A-Za-z .-]{1,30})\)/gi

const visibleLine = (seg) => seg.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/\s+/g, ' ').trim()
const visibleText = (text) => String(text ?? '').replace(/<br\s*\/?>|<\/(?:p|li|div|h[1-6]|tr)>/gi, '\n').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&')
const escapeRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Lines that join back byte for byte: plain text at newlines; HTML also at
// <br> and block ends.
function splitLines(content, html) {
  return html
    ? String(content).split(/(?<=<br\s*\/?>|<\/(?:p|li|div|h[1-6])>|\n)/i)
    : String(content).split(/(?<=\n)/)
}

export function hasCarrierChoiceLine(content) {
  const text = String(content ?? '')
  const html = /<[a-z][^>]*>/i.test(text)
  return splitLines(text, html).some((seg) => carrierPickList(visibleLine(seg)))
}

// The tracking entries the reply itself gives: the order's entries whose
// number is in it, then any "NUM (CARRIER)" it writes for a number the order
// does not list.
export function replyTrackingEntries(reply, orderTracking = []) {
  const text = visibleText(reply)
  const found = []
  for (const t of orderTracking ?? []) {
    if (!t?.number || found.some((f) => f.number === t.number)) continue
    if (new RegExp(`(?<![A-Za-z0-9])${escapeRe(t.number)}(?![A-Za-z0-9])`, 'i').test(text)) found.push({ number: String(t.number), carrier: t.carrier ?? null })
  }
  for (const m of text.matchAll(ANNOTATED_NUMBER_RE)) {
    if (!/\d/.test(m[1]) || found.some((f) => f.number.toUpperCase() === m[1].toUpperCase())) continue
    if (customerCarrierName(m[2])) found.push({ number: m[1], carrier: m[2].trim() })
  }
  return found
}

// The one customer-facing carrier of a set of tracking entries:
// { carrier } or { carrier: null, why }.
export function singleCustomerCarrier(entries) {
  const list = (entries ?? []).filter((t) => t?.number)
  if (!list.length) return { carrier: null, why: 'no tracking' }
  const unknown = [...new Set(list.filter((t) => !customerCarrierName(t.carrier)).map((t) => (t.carrier ? String(t.carrier) : 'no carrier')))]
  if (unknown.length) return { carrier: null, why: `carrier not known (${unknown.join(', ')})` }
  const names = [...new Set(list.map((t) => customerCarrierName(t.carrier)))]
  if (names.length > 1) return { carrier: null, why: `mixed carriers (${names.join(', ')})` }
  return { carrier: names[0] }
}

// content: the reply (plain text or HTML). orderTracking: every tracking
// entry the order holds, [{ number, carrier }]. currentTracking: the current
// shipment's (defaults to orderTracking).
// Returns { content, attempted, carrier, source ('reply' | 'order'), why }.
// attempted false: there is no carrier choice line. carrier null: the line
// was left, why says so.
export function resolveCarrierChoiceLines(content, { orderTracking = [], currentTracking = null, html } = {}) {
  const text = String(content ?? '')
  const isHtml = html ?? /<[a-z][^>]*>/i.test(text)
  const segs = splitLines(text, isHtml)
  if (!segs.some((seg) => carrierPickList(visibleLine(seg)))) return { content: text, attempted: false, carrier: null, source: null, why: null }

  const fromReply = replyTrackingEntries(text, orderTracking)
  let pick
  let source
  if (fromReply.length) {
    pick = singleCustomerCarrier(fromReply)
    source = 'reply'
  } else {
    pick = singleCustomerCarrier(currentTracking ?? orderTracking)
    source = 'order'
  }
  if (!pick.carrier) return { content: text, attempted: true, carrier: null, source, why: pick.why }

  let changed = false
  const out = segs.map((seg) => {
    if (!carrierPickList(visibleLine(seg))) return seg
    const next = seg.replace(CARRIER_PICK_RAW_RE, pick.carrier)
    if (next !== seg) changed = true
    return next
  }).join('')
  // A list split by markup ("<b>UPS</b> / USPS") is left for the agent.
  if (!changed) return { content: text, attempted: true, carrier: null, source, why: 'the line could not be edited' }
  return { content: out, attempted: true, carrier: pick.carrier, source, why: null }
}

// The Activity Log / trace line for a resolveCarrierChoiceLines() result, or
// null when there was no carrier choice line.
export function carrierChoiceNote(result) {
  if (!result?.attempted) return null
  if (result.carrier) {
    return `Carrier choice line set to ${result.carrier} (${result.source === 'reply' ? 'the carrier of the tracking numbers in the reply' : "the carrier of the order's tracking"})`
  }
  return `Carrier choice line left for the agent: ${result.why}`
}
